import {
  useCreateConnection,
  useDeleteConnection,
  useReplaceConnection,
} from "@/lib/data/react-query/connections/connection";
import type { Matter } from "@/lib/types";
import {
  createMatterPartyConnectionInput,
  getMatterPartyConnectionRole,
  getMatterPartyConnections,
  toMatterContactConnectionPresentation,
  type SupportedMatterPartyRole,
} from "@/lib/utils/connections/matter-party-connections";

export const useMatterReferralContacts = (matter: Matter) => {
  const create = useCreateConnection();
  const replace = useReplaceConnection();
  const remove = useDeleteConnection();
  const partyConnections = getMatterPartyConnections(matter);
  const referralConnection = partyConnections.find(
    (item) => getMatterPartyConnectionRole(item) === "Referral",
  );
  const refereeConnection = partyConnections.find(
    (item) => getMatterPartyConnectionRole(item) === "Referee",
  );
  const referral = referralConnection
    ? toMatterContactConnectionPresentation(referralConnection, matter.id)
    : undefined;
  const referee = refereeConnection
    ? toMatterContactConnectionPresentation(refereeConnection, matter.id)
    : undefined;

  const save = async (values: {
    referralContact?: { id: string };
    refereeContact?: { id: string };
  }) => {
    for (const [role, existing, selected] of [
      ["Referral", referralConnection, values.referralContact],
      ["Referee", refereeConnection, values.refereeContact],
    ] as const) {
      if ((existing?.targetObjectId ?? "") === (selected?.id ?? "")) continue;
      if (existing && !selected?.id) {
        await remove.mutateAsync({
          connectionId: existing.id,
          matterId: matter.id,
        });
      } else if (existing && selected?.id) {
        await replace.mutateAsync({
          connectionId: existing.id,
          matterId: matter.id,
          dateModified: existing.dateModified,
          operation: "replace",
          replacement: createMatterPartyConnectionInput({
            contactId: selected.id,
            matterId: matter.id,
            role: role as SupportedMatterPartyRole,
          }),
        });
      } else if (selected?.id) {
        await create.mutateAsync({
          ...createMatterPartyConnectionInput({
            contactId: selected.id,
            matterId: matter.id,
            role: role as SupportedMatterPartyRole,
          }),
          matterId: matter.id,
        });
      }
    }
  };

  return { referral, referee, save };
};
